import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Container, Stack, Typography, Link, Divider } from "@mui/material";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Políticas de privacidad | NovaXFit</title>
        <meta
          name="description"
          content="Consulta las políticas de privacidad de NovaXFit sobre uso de datos personales, membresías, código QR, validación de identidad, beneficios y plataforma web."
        />
        <link rel="canonical" href="https://novaxfit.com/politicas-de-privacidad" />
      </Helmet>

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          bgcolor: "#ffffff",
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3.2}>
            <Stack spacing={1.4}>
              <Typography
                sx={{
                  fontSize: { xs: 28, md: 36 },
                  fontWeight: 800,
                  color: "#111111",
                  lineHeight: 1.12,
                }}
              >
                Políticas de privacidad
              </Typography>

              <Typography
                sx={{
                  fontSize: 15.5,
                  fontWeight: 500,
                  color: "rgba(17,17,17,0.72)",
                  lineHeight: 1.8,
                }}
              >
                En NovaXFit valoramos la privacidad y la seguridad de la información personal de nuestros usuarios.
                Esta política explica cómo recopilamos, usamos, protegemos y administramos los datos registrados dentro
                de nuestra plataforma web y en los procesos relacionados con membresías, validación de identidad,
                beneficios con aliados estratégicos y funcionamiento administrativo del gimnasio.
              </Typography>
            </Stack>

            <Divider />

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                1. Responsable del tratamiento
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit es responsable del tratamiento de los datos personales que el usuario registre o actualice
                dentro de la plataforma, así como de aquellos necesarios para la administración de membresías,
                identificación del titular, control de asistencia, activación de código QR y validación de beneficios.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Para consultas relacionadas con privacidad, actualización de información o validación de identidad,
                el usuario puede comunicarse directamente al número <strong>0985224301</strong>.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                2. Información que podemos recopilar
              </Typography>

              <Box component="ul" sx={{ pl: 2.8, m: 0 }}>
                {[
                  "Nombres y apellidos",
                  "Correo electrónico",
                  "Número o tipo de documento de identificación",
                  "Fotografía del usuario para fines de reconocimiento",
                  "Datos de la membresía activa",
                  "Historial de validaciones del código QR",
                  "Información mínima necesaria para seguimiento administrativo y de asistencia",
                  "Datos enviados por el usuario al actualizar su perfil",
                ].map((item) => (
                  <li key={item}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit no solicitará contraseñas por canales informales, ni pedirá datos bancarios, claves de tarjetas
                ni otra información financiera sensible para el uso ordinario de la plataforma.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                3. Finalidad del tratamiento
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                La información recopilada será utilizada únicamente para fines legítimos relacionados con el funcionamiento
                de NovaXFit, entre ellos:
              </Typography>

              <Box component="ul" sx={{ pl: 2.8, m: 0 }}>
                {[
                  "Crear y administrar cuentas de usuario",
                  "Validar la identidad del titular de la membresía",
                  "Activar y verificar el código QR personal",
                  "Permitir el acceso a beneficios ofrecidos por aliados estratégicos",
                  "Realizar controles internos de asistencia, membresías y seguimiento administrativo",
                  "Brindar soporte cuando el usuario solicite actualizaciones o correcciones de sus datos",
                  "Prevenir usos indebidos, suplantación de identidad o fraude",
                  "Mejorar la organización operativa y administrativa del gimnasio",
                ].map((item) => (
                  <li key={item}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                4. Fotografía, documento y validación de identidad
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Para acceder correctamente a beneficios de aliados o a ciertos procesos de validación, el usuario deberá
                mantener una fotografía clara y actualizada en su perfil. Esto permite que partners o personal autorizado
                puedan verificar visualmente que quien presenta la membresía sea su verdadero titular.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Además, el usuario deberá registrar un documento de identificación válido, como cédula o pasaporte.
                NovaXFit o cualquiera de sus aliados autorizados podrá solicitar la exhibición del documento para confirmar
                la identidad del titular al momento de hacer uso de una membresía o beneficio.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Las actualizaciones del documento de identificación deberán gestionarse directamente con un miembro del staff
                del gimnasio, con el fin de reducir riesgos de falsificación o suplantación.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                5. Uso del código QR
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El código QR de membresía es un identificador digital personal. Su finalidad es facilitar la validación de
                membresías activas y el acceso a beneficios autorizados. Debe ser protegido por el usuario y no debe
                compartirse con terceros.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit podrá limitar, suspender o revisar el uso del código QR cuando existan indicios razonables de uso
                indebido, inconsistencias de identidad o intento de acceso por parte de una persona distinta al titular.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                6. Compartición y terceros
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit no vende la información personal de sus usuarios a terceros. Los datos se utilizan únicamente para
                la gestión interna del gimnasio y la validación de beneficios relacionados con el servicio.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Cuando un aliado estratégico participe en la validación de un beneficio, solo podrá acceder a la información
                mínima necesaria para confirmar la existencia de una membresía activa y validar la identidad del titular.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Los beneficios de partners están sujetos a sus propios términos y condiciones, los cuales se encuentran
                detallados en:
              </Typography>

              <Link
                href="https://novaxfit.com/nuestros-aliados"
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  fontSize: 15.3,
                  fontWeight: 700,
                  color: "#c4121f",
                  wordBreak: "break-word",
                }}
              >
                https://novaxfit.com/nuestros-aliados
              </Link>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                7. Seguridad de la información
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit adopta medidas razonables de seguridad técnicas y organizativas para proteger la información frente
                a accesos no autorizados, uso indebido, pérdida, alteración o divulgación no permitida. Sin embargo, ningún
                sistema conectado a internet puede garantizar seguridad absoluta, por lo que el usuario también debe actuar
                con diligencia respecto de sus credenciales y datos.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                8. Conservación de datos
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Los datos personales serán conservados durante el tiempo necesario para cumplir con las finalidades
                administrativas, operativas, de seguridad y de validación vinculadas a la relación del usuario con NovaXFit,
                o mientras exista una obligación legal o necesidad legítima de conservación.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                9. Derechos del titular
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El titular de los datos puede solicitar información, corrección o actualización de sus datos personales, así
                como ejercer los derechos que correspondan conforme a la normativa aplicable.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Para gestionar una solicitud relacionada con sus datos personales, el usuario podrá comunicarse al
                <strong> 0985224301</strong>.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                10. Exactitud de la información
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El usuario es responsable de proporcionar información veraz, actualizada y completa. NovaXFit no se hace
                responsable por errores, retrasos o restricciones de validación derivados de datos incompletos, desactualizados
                o incorrectos proporcionados por el titular.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                11. Compras en la tienda
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Las compras realizadas desde la tienda web de NovaXFit se coordinan por WhatsApp con el número de
                administración del gimnasio. El acuerdo sobre entrega, disponibilidad final y método de pago se define
                directamente entre el usuario y el personal autorizado del gimnasio.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                12. Cambios a esta política
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit podrá actualizar esta política cuando sea necesario para mejorar el servicio, adecuarse a cambios
                operativos, tecnológicos o normativos. La versión vigente será la publicada en el sitio web.
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
}